import React, { ComponentProps, PropsWithChildren, useContext, useState } from 'react';
import { SecurityFeatureBaseProps } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/securityFeatures.utils.ts';
import {
	AUTHORISED_CAPITAL_FORM_FIELD,
	useAuthorisedCapitalController,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/AuthorisedCapital/useAuthorisedCapitalController.ts';
import {
	useTranslationCapManage,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useMutation } from '@tanstack/react-query';
import { updateSecurityAuthorisedCapital } from '@dealstack/frontend/shared/dataExchange/services/securities.ts';
import { SecurityAuthorisedCapitalFormDto } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/AuthorisedCapital/authorisedCapital.zod.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { FormProvider } from 'react-hook-form';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader.tsx';
import { Flex } from '@mantine/core';
import { DetailEditableField } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/DetailEditableField.tsx';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { NumberInputWithHash } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/NumberInputWithHash/NumberInputWithHash.tsx';
import { EditableInfoContainer } from '@dealstack/frontend/shared/components/EditableInfoContainer/EditableInfoContainer.tsx';
import { EditableInfoContainerGrid } from '@dealstack/frontend/shared/components/EditableInfoContainer/styled.tsx';
import { formatNumberToUI } from '@dealstack/shared/lib/format/number.ts';
import { AuditContext } from '@dealstack/frontend/shared/components/Audit/useAuditController.ts';
import { SecurityIssuedIn } from '@dealstack/shared/lib/generated';

const NumberInputWithHashTyped = (
	props: ComponentProps<typeof NumberInputWithHash<SecurityAuthorisedCapitalFormDto>>
) => <NumberInputWithHash<SecurityAuthorisedCapitalFormDto> {...props} />;

export const AuthorisedCapital = ({
	security,
	onCancel,
	refreshDetails,
	hasEditorPermission = true,
}: PropsWithChildren<SecurityFeatureBaseProps>) => {
	const tCommonUi = useTranslationCommonUi();
	const tCapManage = useTranslationCapManage();
	const [isEdit, setIsEdit] = useState(!security?.authorisedCapital);
	const { authorisedCapitalForm, resetForm } = useAuthorisedCapitalController(
		security,
		tCapManage
	);

	const { refetchSecurityAudit } = useContext(AuditContext);

	const updateAuthorisedCapitalRequest = useMutation({
		mutationFn: updateSecurityAuthorisedCapital,
	});
	const isLoading = updateAuthorisedCapitalRequest.isPending;

	const authorisedCapitalViewField =
		typeof security?.authorisedCapital === 'number'
			? formatNumberToUI(security.authorisedCapital)
			: LONG_DASH;
	const authorisedCapitalLabel = tCapManage(
		'securities.authorisedCapital.authorisedCapital.label',
		{
			defaultValue: 'Authorised capital',
		}
	);
	let authorisedCapitalIssuedInLabel = authorisedCapitalLabel;

	if (security) {
		const issuedInText =
			security.issuedIn === SecurityIssuedIn.UNITS ? tCommonUi('units') : security.currency;

		authorisedCapitalIssuedInLabel = `${authorisedCapitalLabel} (${issuedInText})`;
	}

	const handleUpdateAuthorisedCapital = (args: SecurityAuthorisedCapitalFormDto) => {
		const securityId = security?.id;

		if (!securityId) {
			throw Error('Expect securityId to be defined');
		}

		updateAuthorisedCapitalRequest.mutate(
			{
				securityId,
				body: {
					authorisedCapital: Number(args.authorisedCapital),
				},
			},
			{
				onSuccess: () => {
					showToastV2({
						title: tCapManage('toasts.success.authorised_capital_updated_successfully'),
					});

					refreshDetails?.(securityId);
					void refetchSecurityAudit();
					setIsEdit(false);
				},
			}
		);
	};
	const handleDeleteAuthorisedCapital = () => {
		const securityId = security?.id;

		if (!securityId) {
			throw Error('Expect securityId to be defined');
		}

		updateAuthorisedCapitalRequest.mutate(
			{
				securityId,
				body: {
					authorisedCapital: null,
				},
			},
			{
				onSuccess: () => {
					showToastV2({
						title: tCapManage('toasts.success.authorised_capital_deleted_successfully'),
					});
					refreshDetails?.(securityId);
					void refetchSecurityAudit();
					setIsEdit(false);
				},
			}
		);
	};
	const handleCancel = () => {
		resetForm();
		onCancel?.();
	};

	return (
		<EditableInfoContainer
			title={tCapManage('securities.authorisedCapital.title', {
				defaultValue: 'Authorised capital',
			})}
			isEdit={isEdit}
			setIsEdit={setIsEdit}
			disableButtons={isLoading}
			onUpdate={authorisedCapitalForm.handleSubmit(handleUpdateAuthorisedCapital)}
			onRemove={handleDeleteAuthorisedCapital}
			onCancel={handleCancel}
			hideRemove={!security?.authorisedCapital}
			hasEditorPermission={hasEditorPermission}
			updateBtnDataTestId={TEST_ELEMENT_IDS.CAP_SECURITY_DETAILS_FEATURE_ROW_UPDATE_BUTTON}
		>
			<FormProvider {...authorisedCapitalForm}>
				{isLoading && <InlineLoader />}
				<form
					onSubmit={authorisedCapitalForm.handleSubmit(handleUpdateAuthorisedCapital)}
					style={{ width: '100%' }}
				>
					<EditableInfoContainerGrid $gridColumns={4}>
						<Flex align="flex-start" gap="1rem" wrap="wrap" className="form-flex">
							<DetailEditableField
								editMode={isEdit}
								label={authorisedCapitalIssuedInLabel}
								formInput={
									<NumberInputWithHashTyped
										name={AUTHORISED_CAPITAL_FORM_FIELD.authorisedCapital}
										control={authorisedCapitalForm.control}
										data-testid={
											TEST_ELEMENT_IDS.CAP_AUTHORISED_CAPITAL_AUTHORISED_CAPITAL_INPUT
										}
									/>
								}
								data={authorisedCapitalViewField}
							/>
						</Flex>
					</EditableInfoContainerGrid>
				</form>
			</FormProvider>
		</EditableInfoContainer>
	);
};
